import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { AlertTriangle, LockKeyhole, RefreshCw, ShieldCheck, UserRoundX } from 'lucide-react';
import { toast } from 'sonner';

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('id-ID');
};

const statusLabel = (value) => {
  if (value === 'success') return 'Berhasil';
  if (value === 'failed') return 'Gagal';
  if (value === 'locked') return 'Lockout';
  if (value === 'blocked') return 'Diblokir';
  return value || '-';
};

const statusClass = (value) => {
  if (value === 'success') return 'bg-emerald-100 text-emerald-700';
  if (value === 'failed') return 'bg-rose-100 text-rose-700';
  if (value === 'locked' || value === 'blocked') return 'bg-amber-100 text-amber-700';
  return 'bg-zinc-100 text-zinc-700';
};

const SecurityCenter = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [overview, setOverview] = useState(null);
  const [events, setEvents] = useState([]);

  const fetchData = async () => {
    try {
      const res = await api.get('/admin/security/events');
      setOverview(res.data.overview || null);
      setEvents(res.data.items || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memuat data security');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
  };

  if (!isAdmin) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-muted-foreground">Akses ditolak. Hanya administrator yang bisa mengakses halaman ini.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="security-center-page">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Security Center</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Pantau lockout login, event sensitif, dan posture security aplikasi.
          </p>
        </div>
        <Button onClick={handleRefresh} disabled={refreshing || loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
              <div>
                <p className="text-xs font-semibold uppercase text-emerald-700">Login Berhasil 24 Jam</p>
                <p className="text-2xl font-black text-emerald-600">{overview?.last_24h?.login_success ?? 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-rose-200 bg-rose-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <UserRoundX className="h-5 w-5 text-rose-600" />
              <div>
                <p className="text-xs font-semibold uppercase text-rose-700">Login Gagal 24 Jam</p>
                <p className="text-2xl font-black text-rose-600">{overview?.last_24h?.login_failed ?? 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <LockKeyhole className="h-5 w-5 text-amber-600" />
              <div>
                <p className="text-xs font-semibold uppercase text-amber-700">Lockout Aktif</p>
                <p className="text-2xl font-black text-amber-600">{overview?.active_lockouts?.length ?? 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-xs font-semibold uppercase text-blue-700">Aksi Sensitif 24 Jam</p>
                <p className="text-2xl font-black text-blue-600">{overview?.last_24h?.privileged_actions ?? 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.2fr_0.8fr]">
        <Card className="border border-border/60 bg-white">
          <CardHeader>
            <CardTitle>Event Security Terbaru</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-zinc-50">
                    <TableHead>Waktu</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>IP</TableHead>
                    <TableHead>Detail</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">Memuat...</TableCell>
                    </TableRow>
                  ) : events.length > 0 ? (
                    events.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="text-sm text-muted-foreground">{formatDate(item.created_at)}</TableCell>
                        <TableCell className="font-medium">{item.event_type || '-'}</TableCell>
                        <TableCell>
                          <span className={`rounded px-2 py-1 text-xs ${statusClass(item.status)}`}>
                            {statusLabel(item.status)}
                          </span>
                        </TableCell>
                        <TableCell>{item.subject_username || item.actor_username || '-'}</TableCell>
                        <TableCell className="font-mono text-xs">{item.ip_address || '-'}</TableCell>
                        <TableCell className="max-w-[320px] break-words text-sm text-muted-foreground">{item.detail || '-'}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">Belum ada event security.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="border border-border/60 bg-white">
            <CardHeader>
              <CardTitle>Security Posture</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">Token Browser</p>
                <p className="mt-1 font-medium">{overview?.security?.token_storage || 'sessionStorage'}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">Lockout Login</p>
                <p className="mt-1 font-medium">
                  {overview?.security?.login_max_failures ?? 5} gagal / {overview?.security?.login_lockout_minutes ?? 15} menit
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">Max Request Body</p>
                <p className="mt-1 font-medium">{overview?.security?.max_request_body_bytes ?? 0} bytes</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">Max Import Rows</p>
                <p className="mt-1 font-medium">{overview?.security?.max_import_rows ?? 0} baris</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">CORS Origins</p>
                <div className="mt-1 space-y-1">
                  {(overview?.security?.cors_origins || []).map((origin) => (
                    <p key={origin} className="rounded bg-zinc-50 px-2 py-1 font-mono text-xs">{origin}</p>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border/60 bg-white">
            <CardHeader>
              <CardTitle>Lockout Aktif</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {(overview?.active_lockouts || []).length > 0 ? (
                overview.active_lockouts.map((item) => (
                  <div key={`${item.scope}-${item.value}`} className="rounded border border-amber-200 bg-amber-50 p-3">
                    <p className="font-medium capitalize">{item.scope}</p>
                    <p className="font-mono text-sm">{item.value}</p>
                    <p className="text-xs text-muted-foreground">Sampai {formatDate(item.until)}</p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">Tidak ada lockout aktif saat ini.</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SecurityCenter;
